import { useCallback, useEffect, useState } from "react";
import { BrowserRouter, Routes, Route, useLocation, useNavigate } from "react-router-dom";
import { MotionConfig } from "framer-motion";
import Lenis from "lenis";
import "./App.css";
import { Toaster } from "sonner";
import { LangProvider } from "./context/LangContext";
import { Preloader } from "./components/site/Preloader";
import { Header } from "./components/site/Header";
import { Hero } from "./components/site/Hero";
import { Sectors } from "./components/site/Sectors";
import { Services } from "./components/site/Services";
import { Audience } from "./components/site/Audience";
import { Method } from "./components/site/Method";
import { Team } from "./components/site/Team";
import { Partners } from "./components/site/Partners";
import { Contact } from "./components/site/Contact";
import { Footer } from "./components/site/Footer";
import { ContactDialog } from "./components/site/ContactDialog";
import { Cursor } from "./components/site/Cursor";
import { Offers } from "./components/site/Offers";
import { ChatWidget } from "./components/site/ChatWidget";
import { PaymentStatus } from "./components/site/PaymentStatus";
import { Marquee, Stats, Showreel } from "./components/site/MotionBlocks";
import { CaseTeaser } from "./components/site/CaseTeaser";
import { DataCustody } from "./components/site/DataCustody";
import { RequestDialog } from "./components/site/RequestDialog";
import { scrollToId } from "./lib/scroll";
import CaseZero from "./pages/CaseZero";

const useLenis = () => {
  useEffect(() => {
    const lenis = new Lenis({ lerp: 0.085, smoothWheel: true });
    window.__lenis = lenis;
    let raf;
    const loop = (t) => { lenis.raf(t); raf = requestAnimationFrame(loop); };
    raf = requestAnimationFrame(loop);
    return () => { cancelAnimationFrame(raf); lenis.destroy(); window.__lenis = null; };
  }, []);
};

/* Scrolls to the hash target when landing on "/#section" from another route */
const HashScroll = ({ ready }) => {
  const { hash, pathname } = useLocation();
  useEffect(() => {
    if (pathname !== "/" || !hash || !ready) return;
    const id = hash.slice(1);
    const tm = setTimeout(() => scrollToId(id), 350);
    return () => clearTimeout(tm);
  }, [hash, pathname, ready]);
  return null;
};

function Home({ ready, onDialog, onRequest }) {
  const navigate = useNavigate();
  return (
    <main>
      <Hero ready={ready} onPrimary={() => scrollToId("offerte")} onSecondary={() => navigate("/case-0")} />
      <Marquee />
      <Sectors />
      <Services />
      <DataCustody />
      <Audience />
      <Showreel />
      <Stats />
      <CaseTeaser />
      <Offers onRequest={onRequest} onProject={() => onDialog("project")} />
      <Method />
      <Team />
      <Partners />
      <Contact />
    </main>
  );
}

function Site() {
  const { pathname } = useLocation();
  const isHome = pathname === "/";
  const [ready, setReady] = useState(!isHome);
  const [dialog, setDialog] = useState(null);
  const [request, setRequest] = useState(null);
  const onDone = useCallback(() => setReady(true), []);
  useLenis();

  useEffect(() => {
    if (!window.__lenis) return;
    if (dialog || request) window.__lenis.stop(); else window.__lenis.start();
  }, [dialog, request]);

  return (
    <div className="relative bg-[#0a0a0b]">
      {isHome && <Preloader onDone={onDone} />}
      <Cursor />
      <HashScroll ready={ready} />
      <Header ready={ready} onContact={() => setDialog("project")} />
      <Routes>
        <Route path="/" element={<Home ready={ready} onDialog={setDialog} onRequest={setRequest} />} />
        <Route path="/case-0" element={<CaseZero onRequest={setRequest} />} />
      </Routes>
      <Footer />
      <ContactDialog type={dialog} onClose={() => setDialog(null)} />
      <RequestDialog offer={request} onClose={() => setRequest(null)} />
      <PaymentStatus />
      <ChatWidget />
      <Toaster position="top-center" toastOptions={{ style: { background: "#0d0d0f", color: "#fafaf7", border: "1px solid rgba(212,175,55,0.35)" } }} />
    </div>
  );
}

export default function App() {
  return (
    <LangProvider>
      <MotionConfig reducedMotion="user">
        <BrowserRouter>
          <Site />
        </BrowserRouter>
      </MotionConfig>
    </LangProvider>
  );
}
